// Canvas rendering — ported from prototype/index.html.
// Logical world is 360x720. Canvas is set up with DPR scaling + letterbox
// so drawings always use logical coords (W,H).
//
// Performance pattern: mug + bartender + bomb đều pre-rendered vào offscreen
// canvas 1 lần (lazy on first render). Hot loop chỉ drawImage — 1 GPU blit
// thay vì 5-10 stroke/fill/gradient ops mỗi sprite. Quan trọng cho stage cao
// khi maxParallel = 3-4 chai cùng spawn → render cost ~5x stage đầu.

const W = 360;
const H = 720;

// Floor gradient cache (gradient bound vào ctx, không thể share giữa main +
// offscreen ctx). Refresh khi DPR resize.
const _gradCache = new WeakMap();
function floorGradient(ctx) {
  let g = _gradCache.get(ctx);
  if (!g) {
    g = ctx.createLinearGradient(0, H - 100, 0, H);
    g.addColorStop(0, 'rgba(147,51,234,0)');
    g.addColorStop(1, 'rgba(255,43,177,.35)');
    _gradCache.set(ctx, g);
  }
  return g;
}

// Sprite atlas — built lazily on first render. drawImage with offscreen canvas
// là free path trên iOS Safari, ~10x rẻ hơn procedural stroke/fill.
let _sprites = null;

// Mug sprite: 1px left + 32 body + 4px right (handle pokes out) = 37 wide.
// 1px top (bubble pokes up) + 42 body = 43 tall.
const MUG_SPRITE_W = 37;
const MUG_SPRITE_H = 43;
const MUG_OFFSET_X = -1; // drawImage(sprite, m.x + offsetX, m.y + offsetY)
const MUG_OFFSET_Y = -1;
// Bartender sprite: character extends 32px above platform (hair top), platform
// 30px tall, total 62.
const BAR_SPRITE_W = 76;
const BAR_SPRITE_H = 62;
const BAR_OFFSET_Y = -32;

function makeOffscreen(w, h, draw) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  draw(c.getContext('2d'));
  return c;
}

function ensureSprites() {
  if (_sprites) return _sprites;
  _sprites = {
    mug_normal: makeOffscreen(MUG_SPRITE_W, MUG_SPRITE_H, (cx) => paintMug(cx, 1, 1, 32, 42, false)),
    mug_golden: makeOffscreen(MUG_SPRITE_W, MUG_SPRITE_H, (cx) => paintMug(cx, 1, 1, 32, 42, true)),
    bomb: makeOffscreen(32, 42, (cx) => paintBomb(cx, 0, 0, 32, 42)),
    bartender_normal: makeOffscreen(BAR_SPRITE_W, BAR_SPRITE_H, (cx) => paintBartender(cx, 0, 32, 76, 30, false)),
    bartender_fever: makeOffscreen(BAR_SPRITE_W, BAR_SPRITE_H, (cx) => paintBartender(cx, 0, 32, 76, 30, true))
  };
  return _sprites;
}

export function setupCanvas(canvas) {
  const info = { scale: 1, offX: 0, offY: 0, dpr: 1, rect: null };

  function resize() {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const sx = (rect.width * dpr) / W;
    const sy = (rect.height * dpr) / H;
    info.scale = Math.min(sx, sy);
    info.offX = ((rect.width * dpr) - W * info.scale) / 2;
    info.offY = ((rect.height * dpr) - H * info.scale) / 2;
    info.dpr = dpr;
    info.rect = rect;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(info.scale, 0, 0, info.scale, info.offX, info.offY);
    // DPR đổi → gradient cache stale (canvas buffer rebuilt).
    _gradCache.delete(ctx);
  }

  // Map logical (lx, ly) → CSS pixels relative to canvas's offset parent (#stage).
  function logicalToScreen(lx, ly) {
    const rect = canvas.getBoundingClientRect();
    const sx = rect.width / W, sy = rect.height / H;
    const s = Math.min(sx, sy);
    const offX = (rect.width - W * s) / 2;
    const offY = (rect.height - H * s) / 2;
    return { x: offX + lx * s, y: offY + ly * s };
  }

  // Map clientX → logical X (used by input).
  function clientToLogicalX(clientX) {
    const rect = canvas.getBoundingClientRect();
    const sx = rect.width / W, sy = rect.height / H;
    const s = Math.min(sx, sy);
    const offX = (rect.width - W * s) / 2;
    const px = clientX - rect.left - offX;
    return px / s;
  }

  resize();
  info.resize = resize;
  info.logicalToScreen = logicalToScreen;
  info.clientToLogicalX = clientToLogicalX;
  return info;
}

export function render(ctx, engine, _info) {
  const canvas = ctx.canvas;
  const sprites = ensureSprites();

  // Clear in device pixels (untransformed)
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.restore();

  // Bottom floor glow
  ctx.fillStyle = floorGradient(ctx);
  ctx.fillRect(0, H - 100, W, 100);

  // Mugs — drawImage là free path
  for (const m of engine.mugs) {
    if (m.kind === 'bomb') {
      ctx.drawImage(sprites.bomb, m.x, m.y);
    } else if (m.kind === 'golden') {
      ctx.drawImage(sprites.mug_golden, m.x + MUG_OFFSET_X, m.y + MUG_OFFSET_Y);
    } else {
      ctx.drawImage(sprites.mug_normal, m.x + MUG_OFFSET_X, m.y + MUG_OFFSET_Y);
    }
  }

  // Bartender
  const bt = engine.bartender;
  ctx.drawImage(
    engine.fever ? sprites.bartender_fever : sprites.bartender_normal,
    bt.x,
    bt.y + BAR_OFFSET_Y
  );
}

// === Sprite painters — chạy 1 lần lúc ensureSprites(). Không có shadowBlur
// (canvas shadow blur trên iOS Safari rất đắt). Bù lại neon stroke + saturated
// colors. Visual giữ 95% so với bản cũ. ===

function paintMug(ctx, x, y, w, h, gold) {
  // Handle
  ctx.strokeStyle = gold ? '#fde47a' : 'rgba(245,197,66,.85)';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(x + w - 4, y + h / 2 + 6, 8, -Math.PI / 2, Math.PI / 2);
  ctx.stroke();

  // Body gradient (local coords → vẽ 1 lần, không cache)
  const grad = ctx.createLinearGradient(x, y, x + w, y);
  if (gold) {
    grad.addColorStop(0, '#f5c542');
    grad.addColorStop(0.5, '#fde47a');
    grad.addColorStop(1, '#a87a14');
  } else {
    grad.addColorStop(0, '#7e4a18');
    grad.addColorStop(0.5, '#d18a3a');
    grad.addColorStop(1, '#5a3210');
  }
  ctx.fillStyle = grad;
  roundRect(ctx, x, y + 8, w - 6, h - 8, 4);
  ctx.fill();

  // Foam top
  ctx.fillStyle = 'rgba(255,255,250,.95)';
  roundRect(ctx, x - 1, y, w - 4, 12, 5);
  ctx.fill();
  // Bubbles
  ctx.fillStyle = '#fff';
  ctx.beginPath(); ctx.arc(x + 6,  y + 2,  2.5, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x + 14, y - 1,  1.8, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x + 20, y + 1,  2.2, 0, Math.PI * 2); ctx.fill();

  // Gold border (bright stroke thay vì shadowBlur)
  if (gold) {
    ctx.strokeStyle = 'rgba(253,228,122,.95)';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w - 6, h);
  }
}

function paintBomb(ctx, x, y, w, h) {
  const cx = x + w / 2;
  const cy = y + h / 2 + 4;
  // Body
  ctx.fillStyle = '#1a1a1a';
  ctx.beginPath(); ctx.arc(cx, cy, w / 2 - 4, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = '#ff2bb1';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(cx, cy, w / 2 - 4, 0, Math.PI * 2); ctx.stroke();
  // Fuse
  ctx.strokeStyle = '#f5c542';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx, y + 6);
  ctx.lineTo(cx + 6, y - 2);
  ctx.stroke();
  // Spark — clamp y >= 0 vì sprite không có top padding
  ctx.fillStyle = '#ff5500';
  ctx.beginPath(); ctx.arc(cx + 6, Math.max(0, y - 4), 2.5, 0, Math.PI * 2); ctx.fill();
}

function paintBartender(ctx, x, y, w, h, fever) {
  // Platform với neon stroke (thay shadowBlur 15-25 đắt)
  const grad = ctx.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, '#9333ea');
  grad.addColorStop(1, '#3d1170');
  ctx.fillStyle = grad;
  roundRect(ctx, x, y, w, h, 8);
  ctx.fill();
  // Outline để compensate cho shadowBlur đã bỏ
  ctx.strokeStyle = fever ? 'rgba(245,197,66,.7)' : 'rgba(255,43,177,.6)';
  ctx.lineWidth = 1.5;
  roundRect(ctx, x, y, w, h, 8);
  ctx.stroke();

  // Neon top stroke
  ctx.strokeStyle = fever ? '#f5c542' : '#ff2bb1';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + 4, y);
  ctx.lineTo(x + w - 4, y);
  ctx.stroke();

  // Character silhouette
  const cx = x + w / 2;
  // Body
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.ellipse(cx, y - 6, 10, 14, 0, 0, Math.PI * 2);
  ctx.fill();
  // Head
  ctx.fillStyle = '#ffe0b0';
  ctx.beginPath();
  ctx.arc(cx, y - 22, 7, 0, Math.PI * 2);
  ctx.fill();
  // Bow tie
  ctx.fillStyle = '#ff2bb1';
  ctx.fillRect(cx - 3, y - 12, 6, 3);
  // Hair
  ctx.fillStyle = '#1a1a2e';
  ctx.beginPath();
  ctx.arc(cx, y - 25, 7, Math.PI, Math.PI * 2);
  ctx.fill();

  // Label
  ctx.fillStyle = 'rgba(255,255,255,.85)';
  ctx.font = '700 8px system-ui';
  ctx.textAlign = 'center';
  ctx.fillText('BARTENDER', cx, y + h - 8);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
